import React from "react"
import {
    Button,
    ButtonsContainer,
    Modal,
    ModalBody,
    SectionTitleBody,
    SectionTitleHeading,
} from "@silversea-ssc/ui-components-react"
import { t, Trans } from "@lingui/macro"
import { useECommerceMachine, useSwitchFlowMachine } from "../../app-core/_common/machines/machines-context"
import useModal from "../ModalProvider/useModal"
import { PageViewAnalytics } from "../../app-core/v3/_common/hooks/use-page-view-analytics"
import { useBuildPageDataLayer } from "../../app-core/v3/_common/tracking/page-data-layer"
import SendQuoteSectionIntro from "../../app-core/v3/web-quote/WebQuoteModal/SendQuoteSectionIntro"
import WebQuoteForm from "../WebQuoteForm/WebQuoteForm"
import { useIsTabletOrDesktop } from "../ResponsiveComponents"
import { isWebQuoteSubmitting } from "../../app-core/v3/web-quote/WebQuoteModal/WebQuoteModal"
import { useValidateWebQuoteCommand } from "../../app-core/v3/_common/machines/ecommerce-machine/data-loading/validate-web-quote-command-machine"
import styles from "./WebQuoteExitIntentModal.module.scss"

type Props = {
    // keep-line
}

const WebQuoteExitIntentModal: React.FC<React.PropsWithChildren<Props>> = () => {
    const { closeModal } = useModal()
    const [, sendSF] = useSwitchFlowMachine()
    const [state] = useECommerceMachine()
    const [stateWQ] = useValidateWebQuoteCommand(state)
    const [isRequestAQuote, setIsRequestAQuote] = React.useState(false)
    const isTabletOrDesktop = useIsTabletOrDesktop()

    const activeStep = state.context.flowVersion.tracking.step(
        state.context.activeStep,
        state.context.flowVersion.tracking.subSteps.webQuoteExitIntent,
    )
    const pageDataLayer = useBuildPageDataLayer(activeStep)
    const isSubmitting = isWebQuoteSubmitting(stateWQ)

    return (
        <Modal
            title={t`Get your summary`}
            size="small"
            modal={{
                isOpen: true,
                onRequestClose: () => {
                    if (isSubmitting) return
                    closeModal("close")
                },
                shouldCloseOnEsc: !isSubmitting,
                shouldCloseOnOverlayClick: false,
            }}
            dataContentName="quote-exit-intent-modal-fares"
        >
            <ModalBody variant="wide" className={styles.body}>
                <PageViewAnalytics data={pageDataLayer} />
                {isRequestAQuote && <WebQuoteInlineForm />}
                {!isRequestAQuote && (
                    <>
                        <SectionTitleHeading className={styles.descriptionTitle}>
                            <Trans>Save the quote</Trans>
                        </SectionTitleHeading>
                        <SectionTitleBody className={styles.descriptionBody}>
                            <Trans>
                                Get your personalized quote now! Just provide your email, and we'll send it to you for
                                review
                            </Trans>
                        </SectionTitleBody>
                        <ButtonsContainer mobileFullSize={!isTabletOrDesktop}>
                            <Button
                                renderAs="button"
                                variant="primary"
                                onClick={() => {
                                    sendSF({ type: "SELECT_WEB_QUOTE_FLOW" })
                                    setIsRequestAQuote(true)
                                }}
                                dataContentPiece="request-a-quote-link"
                            >
                                <Trans>Get your summary</Trans>
                            </Button>
                        </ButtonsContainer>
                    </>
                )}
            </ModalBody>
        </Modal>
    )
}

const WebQuoteInlineForm: React.FC = () => {
    const [state] = useECommerceMachine()

    state.context.flowVersion.ui.useAutomaticFareSelection()
    state.context.flowVersion.ui.useAutomaticSuiteCategorySelection()

    return (
        <>
            <SendQuoteSectionIntro />

            <WebQuoteForm formName="exit-intent-send-quote" submitButton={{ content: <Trans>Submit</Trans> }} />
        </>
    )
}

export default WebQuoteExitIntentModal
